import React, { useState } from "react";
import { Button, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import CloseIcon from '@mui/icons-material/Close';

import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { Tag } from "./TagManagementPage";
import Chip from "components/uiElements/dataDisplay/Chip";
import ColorSelector from "components/uiElements/inputs/colorSelector/ColorSelector";
import TagRequestHelper from "./TagRequestHelper";
import { useTranslation } from "react-i18next";

interface TagDialogProps {
  onClose(tagId: string | null | undefined): void;
  tag?: Tag | null;
}

export default function TagDialog(props: TagDialogProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const randomColor = theme.colors.namedColors.colorPalletteArray[10 + Math.floor(Math.random() * 10) % 10];

  const [name, setName] = useState<string>(props.tag?.name ?? "");
  const [description, setDescription] = useState<string>(props.tag?.description ?? "");
  const [color, setColor] = useState<string>(props.tag?.color ?? randomColor);
  const [nameError, setNameError] = useState<string>("");

  const dialogTitle = props.tag?.id ? i18n.t("tag-dialog.title-edit") : i18n.t("tag-dialog.title-new");
  const previewLabel = name || props.tag?.name || i18n.t("tag-dialog.tag-preview-label");
  const saveButtonLabel = props.tag?.id ? i18n.t("tag-dialog.save-edit") : i18n.t("tag-dialog.save-new");

  async function onCreateTag() {
    if (!name) {
      setNameError(i18n.t("tag-dialog.field-name-required-text"));
      return;
    }
    let tagId: string | null | undefined = props.tag?.id;
    if (!props.tag?.id) {
      tagId = await TagRequestHelper.create(name, description, color);
    } else {
      await TagRequestHelper.update(props.tag.id, name, description, color);
    }
    props.onClose(tagId);
  }

  return (
    <ResponsiveDialog maxWidth="xs" open={true}>
      <DialogTitle>
        <Stack direction="row" sx={{
          alignItems: "center"
        }}>
          <Typography variant="h4" sx={{
            flex: 1
          }}>{dialogTitle}</Typography>
          <Button color="secondary" onClick={() => props.onClose(null)} sx={{minWidth: 0, padding: 0}}>
            <CloseIcon />
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack sx={{
          gap: 1
        }}>
          <Stack
            sx={{
              alignItems: "center",
              bgcolor: theme.palette.background.default,
              padding: 1
            }}>
            <Chip color={color} label={previewLabel} />
          </Stack>
          <Box sx={{
            width: "100%"
          }}>
            <Typography variant="h5" sx={{
              paddingBottom: 0.5
            }}>{i18n.t("tag-dialog.field-name-label")}</Typography>
            <TextField fullWidth error={!!nameError} helperText={nameError} onChange={(event) => setName(event.target.value)} placeholder={i18n.t("tag-dialog.field-name-placeholder")} size="small" value={name}></TextField>
          </Box>
          <Box sx={{
            width: "100%"
          }}>
            <Typography variant="h5" sx={{
              paddingBottom: 0.5
            }}>{i18n.t("tag-dialog.field-description-label")}</Typography>
            <TextField fullWidth multiline onChange={(event) => setDescription(event.target.value)} placeholder={i18n.t("tag-dialog.field-description-placeholder")} rows={2} size="small" value={description}></TextField>
          </Box>
          <Box sx={{
            width: "100%"
          }}>
            <Typography variant="h5" sx={{
              paddingBottom: 0.5
            }}>{i18n.t("tag-dialog.field-color-label")}</Typography>
            <ColorSelector color={color} onChange={(color) => setColor(color.toString())} showInput={true} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={() => props.onClose(null)} size="small" variant="contained">
          {i18n.t("tag-dialog.cancel-label")}
        </Button>
        <Button color="success" onClick={onCreateTag} size="small" variant="contained">
          {saveButtonLabel}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}